import { Link } from 'react-router-dom';
import AthleteAvatar from './AthleteAvatar';
import GenderBadge from './GenderBadge';
import { personName } from '../../utils/personName';
import { weightText } from '../../utils/weight';
import { useLabels } from '../../i18n/labels';
import { BADGE_XS } from './badgeSize';

/** Loose union that accepts anything with the fields this card renders.
 *  The card is consumed against mixed shapes (AthleteEntry / AthleteRecord /
 *  Registration join rows) — a single strict type would force every caller
 *  to narrow. All fields are optional so the card degrades gracefully. */
interface AthleteCardView {
  first_name?: string;
  last_name?: string;
  photo_url?: string;
  team_name?: string;
  gender?: string;
  age?: number;
  experience_bouts?: number;
  /** NUMERIC: node-pg sends it as a string ("70.0"). */
  current_weight_kg?: number | string | null;
  country?: string;
}

interface AthleteCardProps {
  athlete: AthleteCardView;
  showTeam?: boolean;
  extraBadges?: React.ReactNode;
  actions?: React.ReactNode;
  compact?: boolean;
  /** #595 — the athlete page: the name is a link that covers the card, and
   *  the actions stay above it (no button inside a link). */
  to?: string;
  /** The history state of the link: the URL of the list (`useListFrom`). */
  linkState?: unknown;
}

export default function AthleteCard({ athlete: a, showTeam = true, extraBadges, actions, compact, to, linkState }: AthleteCardProps) {
  // #531 — "25 m." and "10 kovų" in Lithuanian; the English was hard-coded.
  const { ageShort, boutCount } = useLabels();
  const weight = weightText(a.current_weight_kg);
  return (
    <div data-testid="athlete-card" className={`card ${compact ? 'p-3' : 'p-4'} flex items-start gap-3 ${to ? 'relative hover:shadow-md transition-shadow' : ''}`}>
      {/* #522 — the country flag is on the avatar, at the bottom center. It
          was the first item of the badge row. */}
      <AthleteAvatar
        firstName={a.first_name || '?'}
        lastName={a.last_name || '?'}
        photoUrl={a.photo_url}
        size="md"
        country={a.country}
      />
      <div className="flex-1 min-w-0">
        {to
          ? <Link to={to} state={linkState} className="block font-semibold text-sm truncate after:absolute after:inset-0 after:content-['']">{personName(a.first_name, a.last_name)}</Link>
          : <div className="font-semibold text-sm truncate">{personName(a.first_name, a.last_name)}</div>}
        {showTeam && a.team_name && (
          <div className="text-xs text-gray-500 truncate">{a.team_name}</div>
        )}
        <div className="flex flex-wrap gap-1.5 mt-1">
          {a.gender && <GenderBadge gender={a.gender} short />}
          {a.age != null && a.age > 0 && (
            <span className={`badge badge-gray ${BADGE_XS}`}>{ageShort(Math.floor(a.age))}</span>
          )}
          {a.experience_bouts != null && a.experience_bouts > 0 && (
            <span className={`badge badge-gray ${BADGE_XS}`}>{boutCount(a.experience_bouts)}</span>
          )}
          {/* #552 — weightText() gives "70 kg" on every page; the raw value was "70.0 kg" here. */}
          {weight && <span className={`badge badge-gray ${BADGE_XS}`}>{weight}</span>}
          {extraBadges}
        </div>
      </div>
      {actions && <div className="relative z-10 flex flex-col gap-2 shrink-0">{actions}</div>}
    </div>
  );
}
